const TIME_ZONE_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:\d{2})$/

function hasValidCalendarParts(match) {
  const [, yearValue, monthValue, dayValue, hourValue, minuteValue, secondValue = '0'] =
    match
  const year = Number(yearValue)
  const month = Number(monthValue)
  const day = Number(dayValue)
  const hour = Number(hourValue)
  const minute = Number(minuteValue)
  const second = Number(secondValue)
  const isLeapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
  const daysByMonth = [31, isLeapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

  return (
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    day <= daysByMonth[month - 1] &&
    hour <= 23 &&
    minute <= 59 &&
    second <= 59
  )
}

export function parseEndTimestamp(value) {
  const normalizedValue = String(value || '').trim()

  const match = TIME_ZONE_PATTERN.exec(normalizedValue)

  if (!match || !hasValidCalendarParts(match)) return null

  const timestamp = Date.parse(normalizedValue)
  return Number.isFinite(timestamp) ? timestamp : null
}

export function getStageState(value, now = Date.now()) {
  const endTimestamp = parseEndTimestamp(value)

  if (endTimestamp === null) return 'upcoming'
  return endTimestamp <= now ? 'complete' : 'upcoming'
}

const BaseHTMLElement = globalThis.HTMLElement || class {}

export class AnniversaryTimeline extends BaseHTMLElement {
  connectedCallback() {
    this.abortController?.abort()
    this.abortController = new AbortController()
    this.warnedValues = new Set()
    this.scroller = this.querySelector('[data-anniversary-timeline-scroller]')
    this.stages = Array.from(this.querySelectorAll('[data-anniversary-timeline-stage]'))

    this.scroller?.addEventListener('keydown', (event) => this.handleKeydown(event), {
      signal: this.abortController.signal,
    })
    document.addEventListener('visibilitychange', () => this.handleVisibilityChange(), {
      signal: this.abortController.signal,
    })

    this.refresh()
  }

  disconnectedCallback() {
    this.abortController?.abort()
    this.abortController = null
    this.clearRefreshTimer()
  }

  clearRefreshTimer() {
    if (!this.refreshTimer) return

    clearTimeout(this.refreshTimer)
    this.refreshTimer = null
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.clearRefreshTimer()
      return
    }

    this.refresh()
  }

  handleKeydown(event) {
    if (!this.scroller || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return

    event.preventDefault()

    if (event.key === 'Home') {
      this.scroller.scrollTo({ left: 0, behavior: 'smooth' })
      return
    }

    if (event.key === 'End') {
      this.scroller.scrollTo({ left: this.scroller.scrollWidth, behavior: 'smooth' })
      return
    }

    const direction = event.key === 'ArrowLeft' ? -1 : 1
    this.scroller.scrollBy({ left: direction * Math.max(this.scroller.clientWidth * 0.72, 240), behavior: 'smooth' })
  }

  refresh() {
    this.clearRefreshTimer()

    const now = Date.now()
    let nextRefreshAt = null
    let currentStage = null

    this.stages.forEach((stage) => {
      const rawEndTime = stage.dataset.endTime
      const endTimestamp = parseEndTimestamp(rawEndTime)
      const state = getStageState(rawEndTime, now)

      stage.dataset.state = state
      stage.classList.toggle('is-complete', state === 'complete')

      if (state === 'upcoming' && currentStage === null) currentStage = stage
      if (endTimestamp !== null && endTimestamp > now) {
        nextRefreshAt = nextRefreshAt === null ? endTimestamp : Math.min(nextRefreshAt, endTimestamp)
      }

      if (rawEndTime && endTimestamp === null && !this.warnedValues.has(rawEndTime)) {
        this.warnedValues.add(rawEndTime)
        console.warn(`Anniversary timeline ignored an invalid timezone-aware ISO timestamp: ${rawEndTime}`)
      }
    })

    this.stages.forEach((stage) => {
      if (stage === currentStage) stage.setAttribute('aria-current', 'step')
      else stage.removeAttribute('aria-current')
    })

    if (nextRefreshAt !== null && !document.hidden) {
      const maximumDelay = 2_147_000_000
      const delay = Math.min(Math.max(nextRefreshAt - now + 50, 50), maximumDelay)
      this.refreshTimer = setTimeout(() => this.refresh(), delay)
    }
  }
}

if (globalThis.customElements && !globalThis.customElements.get('anniversary-timeline')) {
  globalThis.customElements.define('anniversary-timeline', AnniversaryTimeline)
}
